import { useEffect, useState } from 'react';
import { groupBy } from 'lodash';

import { LLM_MODEL_DEFAULT } from '@/constants';
import loadIcon from '@/helpers/loadIcons';
import { getLLMModels } from '@/services/model.service';
import { updateChatbotLLMModel } from '@/services/chatbot.service';
import { updateDeployment } from '@/services/deployment.service';

import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import { useUserStore } from '@/stores/user/userStore';
import { useParams } from 'react-router-dom';
import useModalStore from '@/stores/modal/modalStore';
import DSpinner from '@/components/Global/DSpinner';
import IconWrapper from '@/shared/components/UI/IconWrapper';

const LLMSelectorMobile = ({
  deploymentId = null,
  currentModelType = null,
  onModelChange,
  onClose,
}) => {
  const params = useParams();
  const { user } = useUserStore();
  const selectedChatbot = useChatbotStore((state) => state.selectedChatbot);
  const [selectedLLM, setSelectedLLM] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const setSelectedLLModel = useChatbotStore(
    (state) => state.setSelectedLLModel
  );
  const llmModels = useChatbotStore((state) => state.llmModels);
  const llmModelsLastFetchedTier = useChatbotStore((state) => state.llmModelsLastFetchedTier);
  const setLLMModels = useChatbotStore((state) => state.setLLMModels);

  const [modelsByCompany, setModelsByCompany] = useState({});
  const [icons, setIcons] = useState({});
  const [iconsLoaded, setIconsLoaded] = useState(false);

  // Fetch LLM models directly from /llms API (cached via Zustand store)
  useEffect(() => {
    if (
      user?.tier_type &&
      (llmModels.length === 0 || llmModelsLastFetchedTier !== user.tier_type)
    ) {
      const fetchModels = async () => {
        try {
          const response = await getLLMModels({ tier_type: user.tier_type });
          setLLMModels(response.data || [], user.tier_type);
        } catch (error) {
          console.error('Failed to fetch LLM models:', error);
          setLLMModels([], user.tier_type);
        }
      };
      fetchModels();
    }
  }, [user?.tier_type, llmModels.length, llmModelsLastFetchedTier, setLLMModels]);

  const models = llmModels;

  const handleChangeModel = async (model) => {
    // Check if model is disabled based on backend availability
    const isModelDisabled = !model.available;

    if (isModelDisabled) {
      // Show upgrade modal with model name
      const dynamicFeature = `llm_model:${model.label}`;
      useModalStore.getState().openPlansModal(dynamicFeature);
      return;
    }

    setSelectedLLM(model);

    try {
      setIsLoading(true);

      // If deploymentId is provided, update the deployment's model
      if (deploymentId) {
        await updateDeployment(deploymentId, params.id, {
          model_type: model.value,
        });

        // Notify parent component if callback provided
        if (onModelChange) {
          onModelChange(model);
        }
      } else {
        // Otherwise, update the chatbot's default model
        setSelectedLLModel(model);
        await updateChatbotLLMModel({
          kb_id: params?.id,
          llmModel: model.value
        });
      }

      // Close the mobile selector after selection
      if (onClose) {
        onClose();
      }
    } catch (e) {
      console.error('Failed to update LLM model', e);
      // Revert on error
      const previousModel = deploymentId
        ? models.find(m => m.value === currentModelType)
        : selectedChatbot?.last_model_used;
      setSelectedLLM(previousModel || LLM_MODEL_DEFAULT);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadModels = async () => {
      const groupedModels = groupBy(models, 'Logo');

      const iconComponents = {};

      for (const company of Object.keys(groupedModels)) {
        try {
          const iconCompany = await loadIcon(company);
          iconComponents[company] = iconCompany;
        } catch (e) {
          console.error(`Failed to load icon ${company}`, e);
        }
      }

      setIcons(iconComponents);
      setModelsByCompany(groupedModels);
      setIconsLoaded(true);
    };
    if (models?.length) {
      loadModels();
    }
  }, [models]);

  // Set initial selected model for deployment rows (deployment-specific)
  useEffect(() => {
    if (deploymentId && currentModelType && models?.length > 0) {
      const initialModel = models.find(m => m.value === currentModelType);
      setSelectedLLM(initialModel || LLM_MODEL_DEFAULT);
    }
  }, [models, deploymentId, currentModelType]);

  // Set initial selected model for chatbot-level selector (only when NOT a deployment)
  useEffect(() => {
    if (!deploymentId && models?.length > 0) {
      const initialModel = selectedChatbot?.last_model_used;
      setSelectedLLM(initialModel || LLM_MODEL_DEFAULT);
    }
  }, [models, deploymentId, selectedChatbot?.last_model_used]);

  // Show loading spinner while models are being loaded or icons are not ready
  if (!models || models.length === 0 || !iconsLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <DSpinner style={{ height: 32, width: 32 }} />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col  h-full min-h-0">
      {/* Title */}
      <div className="flex flex-col w-full flex-shrink-0 px-v3-sm py-v3-md">
        <p className="text-v3-black-max text-v3-h6 font-bolder">Test with</p>
      </div>

      {/* Models List */}
      <div className="flex-1 overflow-y-auto px-0 py-0 scrollbar-hide">
        <div className="">
          {Object.keys(modelsByCompany)?.map((company) => {
            const Icon = icons[company];

            return (
              <div key={company} className="">
                {modelsByCompany[company].map((model) => {
                  const isModelDisabled = !model.available;
                  const isSelected = selectedLLM?.value === model.value;

                  return (
                    <button
                      key={model.value}
                      onClick={() => handleChangeModel(model)}
                      disabled={isLoading || isModelDisabled}
                      className={`w-full h-[48px] flex items-center gap-v3-xxs px-v3-sm py-v3-xs transition-colors ${
                        isSelected
                          ? 'bg-v3-menu-selected'
                          : 'bg-white hover:bg-v3-menu-selected'
                      } ${
                        isModelDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      } `}
                    >
                      {Icon && (
                        <IconWrapper size={24}>
                          <Icon className="text-gray-700" />
                        </IconWrapper>
                      )}
                      <span className="text-v3-black-max font-medium text-sm flex-1 text-left">
                        {model.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LLMSelectorMobile;